/**
 * Tiny fetch wrapper. Always sends credentials (cookies) and parses JSON.
 *
 * For Phase 5 we authenticate via the `coauthor_session` HttpOnly cookie set
 * by POST /api/auth/skarp_callback. No bearer header needed for browser flows.
 *
 * If a request returns 401, callers should redirect the user to /login.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    super(message ?? `API ${status}`);
  }
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const ct = res.headers.get("content-type") ?? "";
  const looksJson = ct.toLowerCase().includes("application/json");
  let parsed: unknown = null;
  if (text && looksJson) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    throw new ApiError(res.status, parsed ?? text);
  }
  if (!looksJson) {
    // Successful response but not JSON — almost certainly a misrouted request
    // (e.g. SPA index.html served on /api/* when Caddy isn't proxying). Fail
    // loudly rather than feed `undefined` into typed callers.
    throw new ApiError(
      res.status,
      text.slice(0, 200),
      `expected application/json, got ${ct || "(unset)"}`,
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

// ---- Typed endpoint signatures ----------------------------------------------

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  last_seen_at?: string | null;
}

export interface MeResponse {
  ok: true;
  user: User;
}

export interface SkarpCallbackResponse {
  ok: true;
  user: User;
}

export interface AuthConfigResponse {
  ok: true;
  google_client_id: string | null;
}

export interface RegisterResponse {
  ok: true;
  message: string;
  /** True when Skarp sent a verification email and we did NOT set a session
   *  cookie. The SPA should show a "check your email" prompt and redirect
   *  to /login afterwards. */
  requires_verification: boolean;
  user_id: string | null;
}

export const auth = {
  me: () => api.get<MeResponse>("/api/auth/me"),
  skarpCallback: (token: string) =>
    api.post<SkarpCallbackResponse>("/api/auth/skarp_callback", { token }),
  logout: () => api.post<{ ok: true }>("/api/auth/logout"),
  config: () => api.get<AuthConfigResponse>("/api/auth/config"),
  google: (idToken: string) =>
    api.post<SkarpCallbackResponse>("/api/auth/google", { id_token: idToken }),
  /** Sign in with email + password. Sets the session cookie on success. */
  emailLogin: (email: string, password: string) =>
    api.post<SkarpCallbackResponse>("/api/auth/login", { email, password }),
  /** Create a new account. Skarp sends a verification email; in that case
   *  the response carries `requires_verification: true` and no cookie is set. */
  register: (input: { email: string; password: string; username?: string }) =>
    api.post<RegisterResponse>("/api/auth/register", input),
  /** Trigger a password-reset email. Always returns ok regardless of whether
   *  the email is registered (we don't leak account existence). */
  forgotPassword: (email: string) =>
    api.post<{ ok: true; message: string }>("/api/auth/forgot_password", {
      email,
    }),
  /** Confirm a verification token from the email link. Returns the Skarp-
   *  shaped envelope so the SPA can branch on success / expired / error. */
  verifyEmail: (token: string) =>
    api.get<{
      ok: boolean;
      status?: "success" | "fail";
      message?: string;
    }>(`/api/auth/verify_email?token=${encodeURIComponent(token)}`),
  /** Set a new password using the token from a forgot-password email. */
  resetPassword: (token: string, password: string) =>
    api.post<{
      ok: boolean;
      status?: "success" | "fail";
      message?: string;
    }>("/api/auth/reset_password", { token, password }),
  /** Re-send the verification email. Always returns ok (no enumeration). */
  resendVerification: (email: string) =>
    api.post<{ ok: true; message: string }>("/api/auth/resend_verification", {
      email,
    }),
};

// ---- Billing (Phase 12) -----------------------------------------------------

export type BillingTier = "free" | "pro" | "heavy" | "internal";
export type BillingPlan = "monthly" | "annual";
export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | null;

/** Qualitative MCP-quota state. Replaces the raw used/limit numbers we used
 *  to expose; the actual cap is now considered internal infrastructure that
 *  we adjust based on real demand. */
export type UsageState =
  | "unlimited"
  | "comfortable"
  | "active"
  | "approaching_limit"
  | "at_limit";

/** Qualitative integrated-model credit state. Same opacity-first principle:
 *  we don't show the raw balance to avoid rationing anxiety; the audit
 *  page (/api/billing/model_reviews) is the surface for specifics. */
export type CreditState = "unlimited" | "healthy" | "low" | "depleted";

export interface ModelDef {
  model_id: string;
  tier: "small" | "mid" | "large";
  provider: "anthropic" | "openai" | "unknown";
  display_name: string;
}

export interface UpgradeRequiredModel extends ModelDef {
  upgrade_required: "pro" | "heavy";
}

export interface BillingMeResponse {
  ok: true;
  tier: BillingTier;
  subscription_status: SubscriptionStatus;
  /** ISO 8601 — when the current paid period ends. After this passes a Pro
   *  user is treated as free; the SPA shows a "Pro until …" pill. */
  tier_until: string | null;
  /** Qualitative state of the user's monthly AI-review quota. Applies only
   *  to tools that call a model; collaboration is unmetered.
   *  Replaces monthly_used / monthly_limit numerics we used to expose. */
  usage_state: UsageState;
  stripe_customer_id_present: boolean;
  /** False if STRIPE_* env vars aren't populated server-side — SPA hides
   *  upgrade UI in that case so we don't redirect to a broken checkout. */
  billing_configured: boolean;
  /** Qualitative integrated-model credit state. */
  credit_state: CreditState;
  /** ISO 8601 — when the next monthly credit reset happens (paid tiers only).
   *  Null when there's no scheduled refresh (internal tier). */
  next_reset_at: string | null;
  /** True if the one-time free-tier trial has already been granted. */
  model_credits_trial_granted: boolean;
  /** Internal tier bypasses the credit system entirely; SPA can hide the
   *  credit state UI. */
  model_credits_unlimited: boolean;
  /** Models the caller can invoke on their current tier. No per-model
   *  credit cost; cost is qualitative ("the platform handles it"). */
  available_models: ModelDef[];
  /** Models the caller can NOT invoke on their current tier, with the
   *  tier they'd need to upgrade to. Used by the SPA to render locked
   *  rows with inline upgrade CTAs. */
  models_requiring_upgrade: UpgradeRequiredModel[];
}

export interface ModelReviewRow {
  id: number;
  document_id: string | null;
  doc_title: string | null;
  section_id: string | null;
  model_id: string;
  model_tier: "small" | "mid" | "large" | string;
  tokens_in: number;
  tokens_out: number;
  credit_cost: number;
  success: boolean;
  error_code: string | null;
  ts: string | null;
}

export const billing = {
  me: () => api.get<BillingMeResponse>("/api/billing/me"),
  modelReviews: (limit = 20) =>
    api.get<{ ok: true; reviews: ModelReviewRow[] }>(
      `/api/billing/model_reviews?limit=${limit}`,
    ),
  /** Mint a Stripe Checkout Session. Caller does `window.location = url`. */
  createCheckout: (plan: BillingPlan) =>
    api.post<{ ok: true; url: string; session_id: string }>(
      "/api/billing/checkout_session",
      { plan },
    ),
  /** Mint a Stripe Customer Portal session (Pro users only). */
  createPortal: () =>
    api.post<{ ok: true; url: string }>("/api/billing/portal_session"),
};

// ---- Documents --------------------------------------------------------------

export type DocumentStatus = "outlining" | "drafting" | "awaiting_humans" | "published";
export type ApprovalPolicy = "all_members" | "all_maintainers" | "owner_only";
export type DocumentVisibility = "public" | "private";
export type DocumentKind = "general" | "recipe" | "qa" | "reference" | "style_guide";
export type DocumentRole =
  | "owner"
  | "maintainer"
  | "editor"
  | "commenter"
  | "viewer"
  // "anonymous" is returned by GET /api/documents/{id} when the caller is
  // not authenticated or not a member, but the doc is public.
  | "anonymous";

export interface TagRef {
  slug: string;
  name: string;
}

export interface DocumentSummary {
  id: string;
  title: string;
  role: DocumentRole;
  global_status: DocumentStatus;
  approval_policy: ApprovalPolicy;
  visibility: DocumentVisibility;
  kind: DocumentKind;
  language: string | null;
  archived: boolean;
  member_count: number;
  tags: TagRef[];
  created_at: string | null;
  updated_at: string | null;
}

export interface DocumentMemberRow {
  /** May be missing on anonymous / non-member views of public docs (the API
   *  redacts member identifiers; only role + joined_at survive). */
  user_id?: string;
  role: DocumentRole;
  joined_at: string | null;
}

export interface OutlineNode {
  id: string;
  title: string;
  status: string;
  summary: string;
  endorsements: string[];
  /** Phase 13 — Stack-Overflow primitives. `score` is the cached aggregate
   *  `len(endorsements) - len(downvoters)`; the SPA reads this directly
   *  instead of recomputing client-side. */
  downvoters: string[];
  score: number;
  claim:
    | { party_id: string; expires_at: string; purpose: string }
    | null;
  child_count: number;
  /** Count of unresolved comments on this section. */
  unresolved_comment_count: number;
  /** Phase 12.6 — owner-locked section. Edits / claims / appends are
   *  refused for everyone but the doc owner. SPA renders a 🔒 badge. */
  locked: boolean;
  children: OutlineNode[];
}

export interface PublishedArtifact {
  url: string;
  doc_format: string;
  location: "s3" | "file";
}

export interface DocumentDetail {
  document: DocumentSummary;
  members: DocumentMemberRow[];
  outline: OutlineNode | null;
  /** Phase 13 — Stack-Overflow accepted-answer pointer. Set on `kind=qa`
   *  docs when the owner has accepted one of the answers. */
  accepted_answer_section_id: string | null;
  /** Set when the document has been published to S3 and a presigned URL is available. */
  published: PublishedArtifact | null;
}

export interface SectionDetail {
  id: string;
  title: string;
  summary: string;
  body: string;
  status: string;
  endorsements: string[];
  /** Phase 13 — Stack-Overflow primitives. */
  downvoters: string[];
  score: number;
  claim:
    | { party_id: string; expires_at: string; purpose: string }
    | null;
  citations: Array<{
    id: string;
    source_doc_id: string;
    locator: string;
    anchor_offset: number | null;
  }>;
  /** Phase 12.6 — owner-locked section. */
  locked: boolean;
  children: Array<{ id: string; title: string; status: string; locked: boolean }>;
}

export interface CommentRow {
  id: string;
  section_id: string;
  anchor_offset: number | null;
  body: string;
  author_party_id: string;
  /** Phase 12.9 — `"model"` for comments written by a platform-integrated
   *  Claude/GPT review (via `request_model_review`). Distinct from
   *  `"agent"` so the SPA can render a model badge. */
  author_kind: "agent" | "human" | "model";
  reply_to: string | null;
  resolved: boolean;
  ts: string;
  /** Populated only for `author_kind === "model"`. Human-readable model
   *  name to render in the badge (e.g. "Claude Sonnet 4.5"). */
  model_name?: string | null;
}

export interface RevisionRow {
  id: string;
  section_id: string | null;
  op: string;
  party_id: string;
  rationale: string;
  ts: string;
  flags: string[];
  before_hash: string | null;
  after_hash: string | null;
}

export const documents = {
  list: () => api.get<{ ok: true; documents: DocumentSummary[] }>("/api/documents"),
  create: (input: {
    title: string;
    approval_policy?: ApprovalPolicy;
    visibility?: DocumentVisibility;
    kind?: DocumentKind;
    language?: string;
    /** Phase 13 — Stack-Overflow tags. 1–5 slugs; required for `kind=qa`. */
    tags?: string[];
    /** Optional initial body for `outline_root`. For `kind=qa` this is
     *  the question body the detail page renders. */
    initial_body?: string;
  }) => api.post<{ ok: true; document: DocumentSummary }>("/api/documents", input),
  get: (id: string) => api.get<{ ok: true } & DocumentDetail>(`/api/documents/${id}`),
  section: (docId: string, sectionId: string) =>
    api.get<{
      ok: true;
      section: SectionDetail;
      comments: CommentRow[];
      revisions: RevisionRow[];
    }>(`/api/documents/${docId}/sections/${sectionId}`),
  listComments: (docId: string, scope: "mine" | "other" | "unresolved" | "all" = "unresolved") =>
    api.get<{ ok: true; comments: CommentRow[] }>(
      `/api/documents/${docId}/comments?scope=${scope}`,
    ),
  activity: (docId: string, limit = 50) =>
    api.get<{
      ok: true;
      revisions: RevisionRow[];
      round_count: number;
      global_status: string;
    }>(`/api/documents/${docId}/activity?limit=${limit}`),

  // Human actions (Phase 7)
  postComment: (
    docId: string,
    input: { section_id: string; body: string; reply_to?: string; anchor_offset?: number },
  ) => api.post<{ ok: true; comment_id: string }>(`/api/documents/${docId}/comments`, input),
  resolveComment: (docId: string, commentId: string) =>
    api.post<{ ok: true; comment_id: string }>(
      `/api/documents/${docId}/comments/${commentId}/resolve`,
    ),
  submit: (docId: string, force = false) =>
    api.post<{ ok: true; global_status: string }>(`/api/documents/${docId}/submit`, { force }),
  approve: (docId: string) =>
    api.post<{ ok: true; global_status: string; approvals: Record<string, boolean | null> }>(
      `/api/documents/${docId}/approve`,
    ),
  revoke: (docId: string, rationale: string) =>
    api.post<{ ok: true; global_status: string }>(`/api/documents/${docId}/revoke`, { rationale }),
  /** Archive (or unarchive) a document. Owner/maintainer only. Archived
   *  documents vanish from /api/discover and 404 for non-members. The action
   *  is logged to moderation_actions for audit. */
  archive: (docId: string, opts?: { reason?: string; unarchive?: boolean }) =>
    api.post<{ ok: true; document_id: string; archived: boolean }>(
      `/api/documents/${docId}/archive`,
      { reason: opts?.reason, unarchive: opts?.unarchive ?? false },
    ),

  // ---- Phase 13: Stack-Overflow primitives -------------------------------

  /** Upvote (Stack-Overflow ▲) a section. Mutually exclusive with downvote. */
  endorseSection: (docId: string, sectionId: string) =>
    api.post<{ ok: true; section_id: string; endorsements: string[]; score: number }>(
      `/api/documents/${docId}/sections/${sectionId}/endorse`,
      {},
    ),
  /** Downvote (Stack-Overflow ▼) a section. Mutually exclusive with upvote. */
  downvoteSection: (docId: string, sectionId: string) =>
    api.post<{ ok: true; section_id: string; downvoters: string[]; score: number }>(
      `/api/documents/${docId}/sections/${sectionId}/downvote`,
      {},
    ),
  /** Owner-only — mark a section as the accepted answer. The section must
   *  be a direct child of `outline_root` (= an answer, not a comment or the
   *  question itself). */
  acceptAnswer: (docId: string, sectionId: string) =>
    api.post<{ ok: true; accepted_answer_section_id: string }>(
      `/api/documents/${docId}/accept_answer`,
      { section_id: sectionId },
    ),
  unacceptAnswer: (docId: string) =>
    api.post<{ ok: true; accepted_answer_section_id: null }>(
      `/api/documents/${docId}/unaccept_answer`,
      {},
    ),
  /** Replace the document's tag set. Owner-only. */
  setTags: (docId: string, slugs: string[]) =>
    api.post<{ ok: true; tags: TagRef[] }>(
      `/api/documents/${docId}/tags`,
      { slugs },
    ),
  /** List the document's current tags. Member-readable. */
  listTags: (docId: string) =>
    api.get<{ ok: true; tags: TagRef[] }>(`/api/documents/${docId}/tags`),
  /** Regroup a `kind=qa` document into question + sorted answers. */
  questionView: (docId: string) =>
    api.get<{
      ok: true;
      document_id: string;
      title: string;
      tags: TagRef[];
      accepted_answer_section_id: string | null;
      question: {
        id: string;
        title: string;
        body: string;
        summary: string;
        score: number;
      };
      answers: Array<{
        id: string;
        title: string;
        body: string;
        summary: string;
        status: string;
        score: number;
        endorsements: string[];
        downvoters: string[];
        primary_author_party_id: string | null;
        /** "model" when written by a platform-integrated reviewer (Claude/GPT
         *  via request_model_review). Otherwise "agent" or "human". */
        author_kind: "agent" | "human" | "model";
        /** Populated only for author_kind === "model". */
        model_name: string | null;
      }>;
    }>(`/api/documents/${docId}/question_view`),
  /** Build a download URL for a doc. Returns a same-origin path the browser
   *  can navigate to directly — the API responds with `Content-Disposition:
   *  attachment` so the browser triggers a save dialog. Auth: public docs
   *  are anonymous-friendly; private docs require the session cookie (carried
   *  automatically because the URL is same-origin). */
  downloadUrl: (docId: string, format: "md" | "docx") =>
    `/api/documents/${docId}/download?format=${format}`,
};

// ---- Connector tokens (Phase 7d) -------------------------------------------

export interface ConnectorTokenRow {
  id: string;
  label: string | null;
  prefix: string;
  document_id: string | null;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

export const connectors = {
  list: (docId: string) =>
    api.get<{ ok: true; tokens: ConnectorTokenRow[] }>(
      `/api/documents/${docId}/connector_tokens`,
    ),
  mint: (docId: string, label?: string) =>
    api.post<{
      ok: true;
      token: string;
      id: string;
      prefix: string;
      label: string | null;
      document_id: string;
      created_at: string;
      warning: string;
    }>(`/api/documents/${docId}/connector_tokens`, { label: label ?? null }),
  revoke: (docId: string, tokenId: string) =>
    api.delete<{ ok: true; revoked: string }>(
      `/api/documents/${docId}/connector_tokens/${tokenId}`,
    ),
};

// Public discovery feed (Phase C — open-repo pivot). No auth required.
export interface DiscoverDoc {
  id: string;
  title: string;
  global_status: DocumentStatus;
  visibility: "public";
  kind: DocumentKind;
  language: string | null;
  tags: TagRef[];
  owner: { display_name: string };
  member_count: number;
  /** Denormalized from the state blob (migration 0010). For kind=qa docs
   *  this is the answer count. */
  answer_count: number;
  /** outline_root.score mirrored from the state blob — the question's net
   *  vote tally. */
  question_score: number;
  created_at: string | null;
  updated_at: string | null;
}

export const discover = {
  list: (params: {
    q?: string;
    status?: DocumentStatus;
    kind?: DocumentKind;
    language?: string;
    tag?: string;
    sort?: "recent" | "newest" | "unanswered" | "most_votes";
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    if (params.kind) qs.set("kind", params.kind);
    if (params.language) qs.set("language", params.language);
    if (params.tag) qs.set("tag", params.tag);
    if (params.sort) qs.set("sort", params.sort);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return api.get<{
      ok: true;
      documents: DiscoverDoc[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/discover${query ? `?${query}` : ""}`);
  },
};

// ---- Tags (Phase 13 — Q&A pivot) ------------------------------------------

export interface TagSummary {
  slug: string;
  name: string;
  description: string | null;
  doc_count: number;
}

export const tags = {
  /** Public list of all tags, ordered by document count desc. */
  list: () => api.get<{ ok: true; tags: TagSummary[] }>("/api/tags"),
  /** Single tag detail + most-recent docs carrying it. */
  get: (slug: string) =>
    api.get<{
      ok: true;
      tag: { slug: string; name: string; description: string | null };
      documents: Array<{
        id: string;
        title: string;
        kind: DocumentKind;
        global_status: DocumentStatus;
        owner: { display_name: string };
        updated_at: string | null;
      }>;
    }>(`/api/tags/${encodeURIComponent(slug)}`),
};

// ---- Cross-doc activity feed (Phase 12.8 — live feed) ---------------------

export type FeedOp =
  | "edit"
  | "create"
  | "comment"
  | "endorse"
  | "status"
  | "submit"
  | "approval"
  | "share"
  | "create_doc"
  | "revoke"
  | "model_review"
  | "help_wanted"
  // Phase 13 — Stack-Overflow Q&A loop.
  | "downvote"
  | "accept"
  | "ask_question"
  | "propose_answer";

export interface FeedEvent {
  id: string;
  /** ISO timestamp of when the event was recorded. Same as `created_at`. */
  ts: string | null;
  actor: {
    /** UUID of the user; null for legacy / system events. Not PII. */
    user_id: string | null;
    /** Display name (or email-prefix fallback, or "anonymous"). */
    display_name: string;
    kind: "human" | "agent";
  };
  op: FeedOp;
  doc: { id: string; title: string };
  /** Null for doc-level events (e.g. `create_doc`). */
  section: { id: string; title: string | null } | null;
  rationale: string | null;
  created_at: string | null;
}

export const feed = {
  list: (limit = 30) =>
    api.get<{ ok: true; events: FeedEvent[] }>(`/api/feed?limit=${limit}`),
};

// User-wide tokens (Phase A — open-repo pivot). Same shape as per-doc tokens
// but with `document_id: null` — they work across any doc the user can access.
// MCP connect URL is `https://coauthor.skarp.app/mcp` (the bare alias for the same user-wide
// server that `/mcp/me/mcp` serves).
export const userTokens = {
  list: () =>
    api.get<{ ok: true; tokens: ConnectorTokenRow[] }>(
      `/api/users/me/connector_tokens`,
    ),
  mint: (label?: string) =>
    api.post<{
      ok: true;
      token: string;
      id: string;
      prefix: string;
      label: string | null;
      document_id: null;
      created_at: string;
      warning: string;
    }>(`/api/users/me/connector_tokens`, { label: label ?? null }),
  revoke: (tokenId: string) =>
    api.delete<{ ok: true; revoked: string }>(
      `/api/users/me/connector_tokens/${tokenId}`,
    ),
};

// ---- Model comparison -----------------------------------------------------
//
// The zero-concept entry point: text in, a thread out. No document has to
// exist first — this creates one. Deliberately usable before the user has
// connected any agent, because requiring a connector to see value is the
// step most people never finish.

export interface CompareAnswer {
  model_id: string;
  model_name: string;
  provider: string;
  review: string;
  tokens_out: number;
  truncated: boolean;
}

export interface CompareResult {
  ok: true;
  persisted: boolean;
  session_id?: string;
  url?: string;
  visibility?: string;
  answers: CompareAnswer[];
  failures: { model_id: string; code: string; error?: string }[];
}

export const compare = {
  run: (body: { text: string; prompt?: string; title?: string; models?: string[] }) =>
    api.post<CompareResult>(`/api/compare`, body),
};

// ---- API keys (CLI / embedders) -------------------------------------------
//
// Distinct from connector tokens: `cik_*` keys are what the `coauthor` CLI
// and the public /v1 API use. Since identity resolution stopped being tied to
// the transport, one key works on the CLI, the REST API and the MCP wire.

export interface ApiKeyRow {
  id: string;
  label: string | null;
  prefix: string;
  tier: string;
  call_count: number;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

export const apiKeys = {
  list: () => api.get<{ ok: true; keys: ApiKeyRow[] }>(`/api/users/me/api_keys`),
  mint: (label?: string) =>
    api.post<{
      ok: true;
      api_key: string;
      id: string;
      prefix: string;
      label: string | null;
      tier: string;
      created_at: string | null;
      warning: string;
    }>(`/api/users/me/api_keys`, { label: label ?? null }),
  revoke: (keyId: string) =>
    api.delete<{ ok: true }>(`/api/users/me/api_keys/${keyId}`),
};

// ---- Source documents (Phase 8) -------------------------------------------

export type SourceKind = "brief" | "transcript" | "reference" | "data";

export interface SourceDocumentRow {
  id: string;
  title: string;
  kind: SourceKind;
  storage: string;
  uploaded_by: string | null;
  created_at: string | null;
  has_inline_body: boolean;
  downloadable: boolean;
}

export const sources = {
  list: (docId: string) =>
    api.get<{ ok: true; sources: SourceDocumentRow[] }>(
      `/api/documents/${docId}/sources`,
    ),

  /** multipart/form-data upload — bypasses our JSON request() helper. */
  upload: async (
    docId: string,
    file: File,
    opts: { kind?: SourceKind; title?: string } = {},
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", opts.kind ?? "reference");
    if (opts.title) fd.append("title", opts.title);
    const res = await fetch(`/api/documents/${docId}/sources`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text;
    }
    if (!res.ok) {
      throw new ApiError(res.status, parsed);
    }
    return parsed as {
      ok: true;
      source: {
        id: string;
        title: string;
        kind: SourceKind;
        storage: string;
        body_length: number;
      };
    };
  },

  delete: (docId: string, sourceId: string) =>
    api.delete<{ ok: true; deleted: string }>(
      `/api/documents/${docId}/sources/${sourceId}`,
    ),

  download: (docId: string, sourceId: string) =>
    api.get<{ ok: true; url?: string; body?: string; kind: "presigned" | "inline" }>(
      `/api/documents/${docId}/sources/${sourceId}/download`,
    ),
};

// ---- Email notification preferences (Phase 1 of notifications) ------------

export interface NotificationPreferences {
  ok: true;
  notifications_enabled: boolean;
  email: string;
}

export const notifications = {
  getPreferences: () =>
    api.get<NotificationPreferences>(`/api/notifications/preferences`),

  setPreferences: (enabled: boolean) =>
    api.patch<NotificationPreferences>(`/api/notifications/preferences`, {
      notifications_enabled: enabled,
    }),
};
